// components/tables/HomeWorksTable.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  TrashIcon,
  EyeIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  PlayCircleIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline";
import {
  getAllhomeWorkBanks,
  deletehomeWorkBank,
  updatehomeWorkBank,
} from "../../api/homeWork";
import { getAllLessons } from "../../api/lesson";
import { getStatementsByLesson } from "../../api/evaluationApi";
import { uploadQuestionsImage } from "../../api/Question";
import QuestionInlineEditor from "../QuestionInlineEditor";
import Loading from "../loading";
import Notification from "../Notification";
import MathContent from "../MathContent";

/* ============================================================
   نقشه‌ها و ثابت‌ها
   ============================================================ */
const gradeMap = {
  1: "پایه اول",
  2: "پایه دوم",
  3: "پایه سوم",
  4: "پایه چهارم",
  5: "پایه پنجم",
  6: "پایه ششم",
};

const bookMap = {
  1: "ریاضی",
  2: "فارسی و نگارش",
  3: "علوم",
  4: "قرآن",
  5: "املا",
  6: "هدیه‌های آسمانی",
  7: "مطالعات اجتماعی",
};

const difficultyMap = { easy: "آسان", medium: "متوسط", hard: "دشوار" };

const contentTypeMap = {
  question: { label: "سوال", color: "bg-blue-100 text-blue-700" },
  education: { label: "آموزش", color: "bg-purple-100 text-purple-700" },
  content: { label: "محتوای درسی", color: "bg-emerald-100 text-emerald-700" },
};

const MAX_OPTIONS = 4;
const MIN_OPTIONS = 2;
const MAX_IMAGE_SIZE = 500 * 1024;

const generateClientId = () =>
  `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const buildEducationalContent = (slide) => {
  let content = slide.educationalContent || "";
  if (slide.educationalImage && !content.includes(slide.educationalImage)) {
    content += `
      <p>
        <img
          src="${slide.educationalImage}"
          alt="تصویر آموزشی"
          style="max-width:100%;height:auto;"
        />
      </p>
    `;
  }
  return content;
};

/* ============================================================
   کامپوننت اصلی
   ============================================================ */
const HomeWorksTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);

  const [allLessons, setAllLessons] = useState([]);

  const [filters, setFilters] = useState({
    search: "",
    contentType: "",
    grade: "",
    book: "",
    difficulty: "",
    isActive: "",
  });

  const [showNotification, setShowNotification] = useState(false);
  const [notificationProps, setNotificationProps] = useState({});

  const [modalMode, setModalMode] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editQuestions, setEditQuestions] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const [statements, setStatements] = useState([]);
  const [statementsLoading, setStatementsLoading] = useState(false);
  const [uploadingSlideKey, setUploadingSlideKey] = useState(null);
  const [slideImageErrors, setSlideImageErrors] = useState({});

  /* ---------- دروس ---------- */
  useEffect(() => {
    const fetchLessons = async () => {
      try {
        const res = await getAllLessons();
        setAllLessons(res?.data?.lessons || []);
      } catch (err) {
        console.error("خطا در دریافت دروس:", err);
      }
    };
    fetchLessons();
  }, []);

  const editFilteredLessons = useMemo(() => {
    if (!editForm?.grade || !editForm?.book) return [];
    return allLessons.filter(
      (lesson) =>
        String(lesson.grade) === String(editForm.grade) &&
        String(lesson.book) === String(editForm.book),
    );
  }, [editForm?.grade, editForm?.book, allLessons]);

  useEffect(() => {
    if (!editForm) return;
    const isValid = editFilteredLessons.some(
      (lesson) => lesson._id === editForm.lessonId,
    );
    if (!isValid && editForm.lessonId) {
      setEditForm((prev) => ({ ...prev, lessonId: "" }));
    }
  }, [editFilteredLessons]);

  /* ---------- داده‌ها ---------- */
  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { page, limit, ...filters };
      Object.keys(params).forEach(
        (key) =>
          (params[key] === "" || params[key] === undefined) &&
          delete params[key],
      );

      const res = await getAllhomeWorkBanks(params);
      if (res.status === 200) {
        setData(res.data.data || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
      } else {
        showError("خطا در دریافت بانک‌های سوالات");
      }
    } catch (err) {
      console.error(err);
      showError("خطا در ارتباط با سرور");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, filters]);

  /* ---------- اعلان‌ها ---------- */
  const showSuccess = (msg) => {
    setNotificationProps({ message: msg, type: "success", duration: 3000 });
    setShowNotification(true);
  };
  const showError = (msg) => {
    setNotificationProps({ message: msg, type: "error", duration: 4000 });
    setShowNotification(true);
  };

  /* ---------- فیلترها ---------- */
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };
  const clearFilters = () => {
    setFilters({
      search: "",
      contentType: "",
      grade: "",
      book: "",
      difficulty: "",
      isActive: "",
    });
    setPage(1);
  };

  /* ---------- حذف ---------- */
  const handleDelete = async (id) => {
    if (!window.confirm("آیا از حذف این بانک مطمئن هستید؟")) return;
    try {
      await deletehomeWorkBank(id);
      showSuccess("بانک با موفقیت حذف شد");
      fetchData();
    } catch (err) {
      showError("خطا در حذف بانک");
    }
  };

  /* ---------- نگاشت ---------- */
  const getMappedValue = (value, map) => {
    if (value && typeof value === "object") {
      if (value._id !== undefined) {
        const found = Object.entries(map).find(
          ([key]) => key === String(value._id),
        );
        if (found) return found[0];
      }
      const byTitle = Object.entries(map).find(
        ([, title]) => title === value.title,
      );
      return byTitle ? byTitle[0] : "";
    }
    return value === null || value === undefined ? "" : String(value);
  };

  /* ---------- مشاهده ---------- */
  const handleView = (bank) => {
    setSelectedBank(bank);
    setModalMode("view");
  };

  /* ---------- ویرایش ---------- */
  const handleEdit = (bank) => {
    setSelectedBank(bank);
    setEditForm({
      contentType: bank.contentType || "question",
      grade: getMappedValue(bank.grade, gradeMap),
      book: getMappedValue(bank.book, bookMap),
      lessonId: bank.lessonId?._id || bank.lessonId || "",
      difficulty: bank.difficulty || "medium",
      isActive: Boolean(bank.isActive),
    });
    setEditQuestions(
      (bank.questions || []).map((q, index) => ({
        ...q,
        _clientId: q._id || generateClientId(),
        order: q.order || index + 1,
        hasQuestion: q.hasQuestion !== undefined ? q.hasQuestion : true,
        hasEducationalContent: Boolean(q.hasEducationalContent),
        questionText: q.questionText || "",
        educationalContent: q.educationalContent || "",
        educationalImage: q.educationalImage || "",
        videoUrl: q.videoUrl || "",
        options: Array.isArray(q.options)
          ? q.options.map((o, i) => ({
              ...o,
              optionId: o.optionId || String(i + 1),
              optionText: o.optionText || "",
            }))
          : [],
        guide: q.guide || "",
        answerDescription: q.answerDescription || "",
        statementId: q.statementId || "",
        defaultScore: q.defaultScore ?? 1,
      })),
    );
    setStatements([]);
    setSlideImageErrors({});
    setModalMode("edit");
  };

  const closeModal = (force = false) => {
    if (savingEdit && !force) return;
    setModalMode(null);
    setSelectedBank(null);
    setEditForm(null);
    setEditQuestions([]);
    setStatements([]);
    setSlideImageErrors({});
    setUploadingSlideKey(null);
  };

  /* ---------- دریافت گزاره‌ها ---------- */
  useEffect(() => {
    if (modalMode !== "edit" || !editForm?.lessonId) {
      setStatements([]);
      return;
    }
    let cancelled = false;
    const fetchStatements = async () => {
      setStatementsLoading(true);
      try {
        const res = await getStatementsByLesson(editForm.lessonId);
        if (!cancelled) setStatements(res?.statements || []);
      } catch (err) {
        console.error("خطا در دریافت گزاره‌ها:", err);
        if (!cancelled) setStatements([]);
      } finally {
        if (!cancelled) setStatementsLoading(false);
      }
    };
    fetchStatements();
    return () => {
      cancelled = true;
    };
  }, [modalMode, editForm?.lessonId]);

  /* ---------- توابع ویرایش اسلایدها ---------- */
  const updateSlideField = (clientId, field, value) => {
    setEditQuestions((prev) =>
      prev.map((q) =>
        q._clientId === clientId ? { ...q, [field]: value } : q,
      ),
    );
  };

  const updateOptionText = (clientId, optionIndex, value) => {
    setEditQuestions((prev) =>
      prev.map((q) =>
        q._clientId === clientId
          ? {
              ...q,
              options: q.options.map((opt, i) =>
                i === optionIndex ? { ...opt, optionText: value } : opt,
              ),
            }
          : q,
      ),
    );
  };

  const addSlide = () => {
    const isQ = editForm?.contentType === "question";
    setEditQuestions((prev) => [
      ...prev,
      {
        _clientId: generateClientId(),
        order: prev.length + 1,
        hasQuestion: isQ,
        hasEducationalContent: !isQ,
        questionText: "",
        educationalContent: "",
        educationalImage: "",
        videoUrl: "",
        options: isQ
          ? [
              { optionId: "1", optionText: "" },
              { optionId: "2", optionText: "" },
            ]
          : [],
        correctAnswer: null,
        guide: "",
        answerDescription: "",
        defaultScore: 1,
        statementId: "",
      },
    ]);
  };

  const removeSlide = (clientId) =>
    setEditQuestions((prev) =>
      prev
        .filter((q) => q._clientId !== clientId)
        .map((q, i) => ({ ...q, order: i + 1 })),
    );

  const addOption = (clientId) => {
    setEditQuestions((prev) =>
      prev.map((q) => {
        if (q._clientId !== clientId) return q;
        const opts = q.options || [];
        if (opts.length >= MAX_OPTIONS) return q;
        const used = opts.map((o) => o.optionId);
        const nextId = ["1", "2", "3", "4"].find((id) => !used.includes(id));
        return {
          ...q,
          options: [...opts, { optionId: nextId, optionText: "" }],
        };
      }),
    );
  };

  const removeOption = (clientId, optionIndex) => {
    setEditQuestions((prev) =>
      prev.map((q) => {
        if (q._clientId !== clientId) return q;
        const opts = q.options || [];
        if (opts.length <= MIN_OPTIONS) return q;
        const removed = opts[optionIndex];
        const newOpts = opts.filter((_, i) => i !== optionIndex);
        return {
          ...q,
          options: newOpts,
          correctAnswer:
            q.correctAnswer === removed?.optionId ? null : q.correctAnswer,
        };
      }),
    );
  };

  const handleSlideImageUpload = async (clientId, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setSlideImageErrors((prev) => ({ ...prev, [clientId]: "" }));

    if (!file.type.startsWith("image/")) {
      setSlideImageErrors((prev) => ({
        ...prev,
        [clientId]: "لطفاً یک فایل تصویری انتخاب کنید.",
      }));
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setSlideImageErrors((prev) => ({
        ...prev,
        [clientId]: `حجم تصویر نباید بیشتر از ۵۰۰ کیلوبایت باشد. حجم فعلی: ${(file.size / 1024).toFixed(0)} کیلوبایت`,
      }));
      return;
    }

    setUploadingSlideKey(clientId);
    try {
      const res = await uploadQuestionsImage(file);
      const url =
        res?.data?.url || res?.data?.imageUrl || res?.data?.data?.url || "";
      if (!url) {
        showError("آدرس تصویر از سرور دریافت نشد.");
        return;
      }
      updateSlideField(clientId, "educationalImage", url);
      showSuccess("تصویر با موفقیت بارگذاری شد.");
    } catch (err) {
      console.error("خطا در آپلود تصویر:", err);
      showError("خطا در بارگذاری تصویر.");
    } finally {
      setUploadingSlideKey(null);
    }
  };

  /* ---------- ذخیره ---------- */
  const handleSaveEdit = async (event) => {
    event.preventDefault();
    if (!selectedBank?._id || !editForm) return;

    if (typeof updatehomeWorkBank !== "function") {
      showError("API ذخیره تغییرات در دسترس نیست.");
      return;
    }

    const isQ = editForm.contentType === "question";

    const questions = editQuestions.map((q, index) => {
      const { _clientId, ...rest } = q;
      const educationalContent = isQ ? "" : buildEducationalContent(rest);

      return {
        ...rest,
        order: index + 1,
        hasQuestion: isQ,
        hasEducationalContent: !isQ,
        questionText: isQ ? rest.questionText || "" : "",
        educationalContent,
        educationalImage: undefined,
        videoUrl: !isQ ? rest.videoUrl || "" : "",
        options: isQ
          ? (rest.options || []).map((o, i) => ({
              ...o,
              optionId: o.optionId || String(i + 1),
            }))
          : [],
        correctAnswer: isQ ? (rest.correctAnswer ?? null) : null,
        guide: isQ ? rest.guide || "" : "",
        answerDescription: isQ ? rest.answerDescription || "" : "",
        statementId: rest.statementId || null,
      };
    });

    const statementIds = [
      ...new Set(editQuestions.map((q) => q.statementId).filter(Boolean)),
    ];

    const payload = {
      contentType: editForm.contentType,
      grade: editForm.grade,
      book: editForm.book,
      lessonId: editForm.lessonId,
      difficulty: editForm.difficulty,
      isActive: editForm.isActive,
      educationalObjective: statementIds,
      questions,
    };

    setSavingEdit(true);
    try {
      await updatehomeWorkBank(selectedBank._id, payload);
      showSuccess("تغییرات بانک با موفقیت ذخیره شد.");
      closeModal(true);
      await fetchData();
    } catch (error) {
      console.error("خطا در ویرایش بانک:", error);
      showError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "ذخیره تغییرات ناموفق بود.",
      );
    } finally {
      setSavingEdit(false);
    }
  };

  /* ============================================================
     رندر
     ============================================================ */
  if (loading && data.length === 0) return <Loading />;

  const isEditQuestionType = editForm?.contentType === "question";
  const isEditEducationType =
    editForm?.contentType === "education" ||
    editForm?.contentType === "content";

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mt-6">
      {showNotification && (
        <Notification
          {...notificationProps}
          setShowNotification={setShowNotification}
          showNotification={showNotification}
        />
      )}

      {/* ================= فیلترها ================= */}
      <div className="p-4 border-b border-gray-200 bg-gray-50/50">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              جستجو
            </label>
            <div className="relative">
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                placeholder="جستجو..."
                className="w-full px-3 py-2 pr-8 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none"
              />
              <MagnifyingGlassIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>

          <div className="min-w-[120px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              نوع محتوا
            </label>
            <select
              value={filters.contentType}
              onChange={(e) =>
                handleFilterChange("contentType", e.target.value)
              }
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="">همه</option>
              <option value="question">سوال</option>
              <option value="education">آموزش</option>
              <option value="content">محتوای درسی</option>
            </select>
          </div>

          <div className="min-w-[120px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              پایه
            </label>
            <select
              value={filters.grade}
              onChange={(e) => handleFilterChange("grade", e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="">همه</option>
              {Object.entries(gradeMap).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-[120px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              کتاب
            </label>
            <select
              value={filters.book}
              onChange={(e) => handleFilterChange("book", e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="">همه</option>
              {Object.entries(bookMap).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-[100px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              سختی
            </label>
            <select
              value={filters.difficulty}
              onChange={(e) => handleFilterChange("difficulty", e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="">همه</option>
              <option value="easy">آسان</option>
              <option value="medium">متوسط</option>
              <option value="hard">دشوار</option>
            </select>
          </div>

          <div className="flex gap-2 self-end pb-1">
            <button
              onClick={clearFilters}
              className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 transition"
            >
              پاک کردن
            </button>
            <button
              onClick={fetchData}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition flex items-center gap-1"
            >
              <ArrowPathIcon className="w-4 h-4" />
              به‌روزرسانی
            </button>
          </div>
        </div>
      </div>

      {/* ================= جدول ================= */}
      <div className="overflow-x-auto">
        <table className="w-full text-right text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {[
                "ردیف",
                "نوع محتوا",
                "پایه",
                "کتاب",
                "درس",
                "سختی",
                "اسلایدها",
                "وضعیت",
                "نویسنده",
                "تاریخ",
              ].map((header) => (
                <th
                  key={header}
                  className="px-4 py-3 text-xs font-medium text-gray-500"
                >
                  {header}
                </th>
              ))}
              <th className="px-4 py-3 text-xs font-medium text-gray-500 text-center">
                عملیات
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan="11"
                  className="px-4 py-8 text-center text-gray-500"
                >
                  هیچ بانکی یافت نشد
                </td>
              </tr>
            ) : (
              data.map((bank, index) => {
                const ct =
                  contentTypeMap[bank.contentType] || contentTypeMap.question;
                return (
                  <tr key={bank._id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 text-gray-500">
                      {(page - 1) * limit + index + 1}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 text-xs rounded-full ${ct.color}`}
                      >
                        {ct.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {bank.grade?.title || gradeMap[bank.grade] || "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {bank.book?.title || bookMap[bank.book] || "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {bank.lessonId?.title || "-"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 text-xs rounded-full ${
                          bank.difficulty === "easy"
                            ? "bg-green-100 text-green-700"
                            : bank.difficulty === "hard"
                              ? "bg-red-100 text-red-700"
                              : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {difficultyMap[bank.difficulty] || bank.difficulty}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">
                      {bank.questions?.length || 0}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 text-xs rounded-full ${
                          bank.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {bank.isActive ? "فعال" : "غیرفعال"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-[80px] truncate">
                      {bank.authorId?.name || "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {bank.createdAt
                        ? new Date(bank.createdAt).toLocaleDateString("fa-IR")
                        : "-"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleView(bank)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="مشاهده"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEdit(bank)}
                          className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded-lg transition"
                          title="ویرایش"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(bank._id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="حذف"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ================= صفحه‌بندی ================= */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50/50">
          <div className="text-xs text-gray-500">
            نمایش {data.length} از {total} مورد
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className={`p-2 rounded-lg border border-gray-200 transition ${
                page === 1
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-gray-100"
              }`}
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <span className="text-sm">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className={`p-2 rounded-lg border border-gray-200 transition ${
                page === totalPages
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-gray-100"
              }`}
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ================= مودال ================= */}
      {modalMode && selectedBank && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-3 sm:p-5"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeModal();
          }}
        >
          <div
            className={`w-full ${
              modalMode === "edit" ? "max-w-4xl" : "max-w-3xl"
            } max-h-[92vh] overflow-y-auto rounded-2xl bg-white shadow-2xl`}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
              <div>
                <h2 className="font-bold text-gray-800">
                  {modalMode === "view"
                    ? "مشاهده بانک محتوا"
                    : "ویرایش بانک محتوا"}
                </h2>
                <p className="mt-1 text-xs text-gray-500">
                  شناسه: {selectedBank._id}
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                disabled={savingEdit}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                aria-label="بستن"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {modalMode === "view" ? (
              /* ============= VIEW ============= */
              <div className="space-y-5 p-4 sm:p-6">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Detail
                    label="نوع محتوا"
                    value={
                      contentTypeMap[selectedBank.contentType]?.label ||
                      selectedBank.contentType ||
                      "-"
                    }
                  />
                  <Detail
                    label="پایه"
                    value={
                      selectedBank.grade?.title ||
                      gradeMap[selectedBank.grade] ||
                      selectedBank.grade?.name ||
                      "-"
                    }
                  />
                  <Detail
                    label="کتاب"
                    value={
                      selectedBank.book?.title ||
                      bookMap[selectedBank.book] ||
                      selectedBank.book?.name ||
                      "-"
                    }
                  />
                  <Detail
                    label="درس"
                    value={selectedBank.lessonId?.title || "-"}
                  />
                  <Detail
                    label="سطح دشواری"
                    value={
                      difficultyMap[selectedBank.difficulty] ||
                      selectedBank.difficulty ||
                      "-"
                    }
                  />
                  <Detail
                    label="وضعیت"
                    value={selectedBank.isActive ? "فعال" : "غیرفعال"}
                  />
                  <Detail
                    label="نویسنده"
                    value={selectedBank.authorId?.name || "-"}
                  />
                  <Detail
                    label="تاریخ ایجاد"
                    value={
                      selectedBank.createdAt
                        ? new Date(selectedBank.createdAt).toLocaleDateString(
                            "fa-IR",
                          )
                        : "-"
                    }
                  />
                </div>

                <div className="space-y-4">
                  <h3 className="border-b pb-2 text-sm font-bold text-gray-800">
                    اسلایدها ({selectedBank.questions?.length || 0})
                  </h3>

                  {(selectedBank.questions || []).length === 0 ? (
                    <p className="text-sm text-gray-500">
                      برای این بانک اسلایدی ثبت نشده است.
                    </p>
                  ) : (
                    selectedBank.questions.map((question, index) => (
                      <div
                        key={question._id || index}
                        className="space-y-3 rounded-xl border border-gray-200 p-4"
                      >
                        <h4 className="text-sm font-bold text-gray-700">
                          اسلاید {question.order || index + 1}
                        </h4>

                        {question.questionText && (
                          <div>
                            <p className="mb-1 text-xs font-semibold text-gray-500">
                              متن سؤال
                            </p>
                            <MathContent
                              html={question.questionText}
                              className="prose prose-sm max-w-none text-sm break-words"
                            />
                          </div>
                        )}

                        {question.educationalContent && (
                          <div>
                            <p className="mb-1 text-xs font-semibold text-gray-500">
                              متن / محتوای آموزشی
                            </p>
                            <MathContent
                              html={question.educationalContent}
                              className="prose prose-sm max-w-none text-sm break-words"
                            />
                          </div>
                        )}

                        {question.options?.length > 0 && (
                          <div>
                            <p className="mb-2 text-xs font-semibold text-gray-500">
                              گزینه‌ها
                            </p>
                            <div className="space-y-2">
                              {question.options.map((option, optionIndex) => (
                                <div
                                  key={
                                    option._id || option.optionId || optionIndex
                                  }
                                  className={`rounded-lg border p-2 text-sm ${
                                    String(question.correctAnswer) ===
                                      String(optionIndex + 1) ||
                                    String(question.correctAnswer) ===
                                      String(option.optionId)
                                      ? "border-green-300 bg-green-50"
                                      : "border-gray-100 bg-gray-50"
                                  }`}
                                >
                                  <div className="flex items-start gap-2">
                                    <span className="font-bold shrink-0">
                                      {option.optionId || optionIndex + 1}.
                                    </span>
                                    <MathContent
                                      html={option.optionText || ""}
                                      className="prose prose-sm max-w-none text-sm break-words flex-1"
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {question.videoUrl && (
                          <div>
                            <p className="mb-1 text-xs font-semibold text-gray-500">
                              لینک ویدیو
                            </p>
                            <a
                              href={question.videoUrl}
                              target="_blank"
                              rel="noreferrer"
                              dir="ltr"
                              className="block break-all text-sm text-blue-600 underline"
                            >
                              {question.videoUrl}
                            </a>
                          </div>
                        )}

                        {question.guide && (
                          <div>
                            <p className="mb-1 text-xs font-semibold text-gray-500">
                              راهنمای سؤال
                            </p>
                            <MathContent
                              html={question.guide}
                              className="prose prose-sm max-w-none text-sm break-words"
                            />
                          </div>
                        )}

                        {question.answerDescription && (
                          <div>
                            <p className="mb-1 text-xs font-semibold text-gray-500">
                              توضیحات پاسخ
                            </p>
                            <MathContent
                              html={question.answerDescription}
                              className="prose prose-sm max-w-none text-sm break-words"
                            />
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
                  >
                    بستن
                  </button>
                </div>
              </div>
            ) : (
              /* ============= EDIT ============= */
              <form onSubmit={handleSaveEdit} className="space-y-5 p-4 sm:p-6">
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm leading-6 text-blue-800">
                  اطلاعات بانک و اسلایدها را ویرایش کنید.
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      نوع محتوا
                    </label>
                    <select
                      value={editForm.contentType}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          contentType: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="question">سؤال</option>
                      <option value="education">آموزش</option>
                      <option value="content">محتوای درسی</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      پایه
                    </label>
                    <select
                      value={editForm.grade}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          grade: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">انتخاب پایه</option>
                      {Object.entries(gradeMap).map(([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      کتاب
                    </label>
                    <select
                      value={editForm.book}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          book: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">انتخاب کتاب</option>
                      {Object.entries(bookMap).map(([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      سطح دشواری
                    </label>
                    <select
                      value={editForm.difficulty}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          difficulty: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="easy">آسان</option>
                      <option value="medium">متوسط</option>
                      <option value="hard">دشوار</option>
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      درس
                    </label>
                    <select
                      value={editForm.lessonId}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          lessonId: e.target.value,
                        }))
                      }
                      disabled={!editForm.grade || !editForm.book}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="">
                        {!editForm.grade || !editForm.book
                          ? "ابتدا پایه و کتاب را انتخاب کنید"
                          : editFilteredLessons.length === 0
                            ? "درسی یافت نشد"
                            : "انتخاب درس"}
                      </option>
                      {editFilteredLessons.map((lesson) => (
                        <option key={lesson._id} value={lesson._id}>
                          {lesson.title}
                        </option>
                      ))}
                    </select>
                    {editForm.grade &&
                      editForm.book &&
                      editFilteredLessons.length === 0 && (
                        <p className="mt-1 text-xs text-amber-600">
                          درسی برای این پایه و کتاب پیدا نشد.
                        </p>
                      )}
                  </div>

                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={editForm.isActive}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          isActive: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    بانک فعال باشد
                  </label>
                </div>

                {/* ================= اسلایدها ================= */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3 border-b border-gray-100 pb-3">
                    <h3 className="font-bold text-gray-800">
                      اسلایدها ({editQuestions.length})
                    </h3>
                    <button
                      type="button"
                      onClick={addSlide}
                      className="rounded-lg bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
                    >
                      + افزودن اسلاید
                    </button>
                  </div>

                  {editQuestions.length === 0 && (
                    <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-500">
                      هنوز اسلایدی وجود ندارد؛ از دکمه افزودن اسلاید استفاده
                      کنید.
                    </p>
                  )}

                  {editQuestions.map((q, index) => {
                    const isUploading = uploadingSlideKey === q._clientId;

                    return (
                      <section
                        key={q._clientId}
                        className="space-y-4 rounded-xl border border-gray-200 p-4"
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="font-bold text-gray-800">
                            اسلاید {index + 1}
                          </h4>
                          <button
                            type="button"
                            onClick={() => removeSlide(q._clientId)}
                            className="rounded-lg px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                          >
                            حذف اسلاید
                          </button>
                        </div>

                        {/* گزاره آموزشی */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            گزاره آموزشی مرتبط
                          </label>
                          {statementsLoading ? (
                            <div className="py-3 text-center text-sm text-gray-500">
                              در حال دریافت گزاره‌ها...
                            </div>
                          ) : !editForm.lessonId ? (
                            <p className="text-xs text-gray-500 py-1">
                              ابتدا درس را انتخاب کنید.
                            </p>
                          ) : statements.length === 0 ? (
                            <p className="text-xs text-gray-500 py-1">
                              گزاره‌ای برای این درس ثبت نشده است.
                            </p>
                          ) : (
                            <div className="border border-gray-200 rounded-xl bg-gray-50 p-3 max-h-40 overflow-y-auto">
                              <div className="flex flex-wrap gap-2">
                                {statements.map((statement) => {
                                  const active =
                                    q.statementId === statement._id;
                                  return (
                                    <button
                                      key={statement._id}
                                      type="button"
                                      onClick={() =>
                                        updateSlideField(
                                          q._clientId,
                                          "statementId",
                                          statement._id,
                                        )
                                      }
                                      className={`px-3 py-2 rounded-lg text-xs font-medium transition ${
                                        active
                                          ? "bg-green-600 text-white"
                                          : "bg-white text-gray-700 border border-gray-200 hover:border-green-400"
                                      }`}
                                    >
                                      {active && "✓ "}
                                      {statement.content}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* فیلدهای سؤال */}
                        {isEditQuestionType && (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                متن سؤال *
                              </label>
                              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                                <QuestionInlineEditor
                                  choosedContent={q.questionText}
                                  content={q.questionText}
                                  setContent={(content) =>
                                    updateSlideField(
                                      q._clientId,
                                      "questionText",
                                      content,
                                    )
                                  }
                                />
                              </div>
                            </div>

                            <div className="space-y-3">
                              <label className="block text-sm font-medium text-gray-700">
                                گزینه‌ها ({q.options?.length || 0} از{" "}
                                {MAX_OPTIONS})
                              </label>

                              {(q.options || []).map((option, optionIndex) => (
                                <div
                                  key={
                                    option._id || option.optionId || optionIndex
                                  }
                                  className="flex items-start gap-2"
                                >
                                  <span className="w-7 pt-3 text-sm font-bold text-gray-600">
                                    {option.optionId || optionIndex + 1}
                                  </span>
                                  <div className="flex-1 min-w-0 bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <QuestionInlineEditor
                                      choosedContent={option.optionText}
                                      content={option.optionText}
                                      setContent={(content) =>
                                        updateOptionText(
                                          q._clientId,
                                          optionIndex,
                                          content,
                                        )
                                      }
                                    />
                                  </div>
                                  {(q.options?.length || 0) > MIN_OPTIONS && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeOption(q._clientId, optionIndex)
                                      }
                                      title="حذف گزینه"
                                      className="p-2 mt-1 text-red-500 hover:bg-red-50 rounded-lg"
                                    >
                                      <XMarkIcon className="w-5 h-5" />
                                    </button>
                                  )}
                                </div>
                              ))}

                              {(q.options?.length || 0) < MAX_OPTIONS && (
                                <button
                                  type="button"
                                  onClick={() => addOption(q._clientId)}
                                  className="text-sm font-medium text-blue-600 hover:text-blue-800"
                                >
                                  + افزودن گزینه
                                </button>
                              )}
                            </div>

                            <div className="border-t border-gray-200 pt-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                پاسخ صحیح *
                              </label>
                              <div className="flex flex-wrap gap-2">
                                {(q.options || []).map(
                                  (option, optionIndex) => {
                                    const active =
                                      q.correctAnswer === option.optionId ||
                                      String(q.correctAnswer) ===
                                        String(optionIndex + 1);
                                    return (
                                      <button
                                        key={option.optionId || optionIndex}
                                        type="button"
                                        onClick={() =>
                                          updateSlideField(
                                            q._clientId,
                                            "correctAnswer",
                                            option.optionId,
                                          )
                                        }
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                                          active
                                            ? "bg-green-600 text-white"
                                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                        }`}
                                      >
                                        گزینه {optionIndex + 1}
                                        {active && " ✓"}
                                      </button>
                                    );
                                  },
                                )}
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                راهنمای سؤال
                              </label>
                              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                                <QuestionInlineEditor
                                  choosedContent={q.guide}
                                  content={q.guide}
                                  setContent={(content) =>
                                    updateSlideField(
                                      q._clientId,
                                      "guide",
                                      content,
                                    )
                                  }
                                />
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                توضیحات پاسخ
                              </label>
                              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                                <QuestionInlineEditor
                                  choosedContent={q.answerDescription}
                                  content={q.answerDescription}
                                  setContent={(content) =>
                                    updateSlideField(
                                      q._clientId,
                                      "answerDescription",
                                      content,
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* فیلدهای آموزش/محتوا */}
                        {isEditEducationType && (
                          <div className="space-y-4 p-4 rounded-xl bg-blue-50/40 border border-blue-200">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                متن{" "}
                                {editForm.contentType === "content"
                                  ? "محتوای درسی"
                                  : "آموزشی"}
                              </label>
                              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                                <QuestionInlineEditor
                                  choosedContent={q.educationalContent}
                                  content={q.educationalContent}
                                  setContent={(content) =>
                                    updateSlideField(
                                      q._clientId,
                                      "educationalContent",
                                      content,
                                    )
                                  }
                                />
                              </div>
                            </div>

                            <div>
                              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                                <PhotoIcon className="w-5 h-5 text-blue-500" />
                                تصویر{" "}
                                {editForm.contentType === "content"
                                  ? "محتوای درسی"
                                  : "آموزشی"}
                              </label>
                              <div className="flex flex-wrap items-center gap-3">
                                <label
                                  className={`relative inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition ${
                                    isUploading
                                      ? "bg-gray-100 text-gray-400"
                                      : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                                  }`}
                                >
                                  <PhotoIcon className="w-4 h-4" />
                                  {isUploading
                                    ? "در حال بارگذاری..."
                                    : "انتخاب تصویر"}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    disabled={isUploading}
                                    onChange={(e) =>
                                      handleSlideImageUpload(q._clientId, e)
                                    }
                                    className="sr-only"
                                  />
                                </label>
                                <span className="text-xs text-gray-500">
                                  حداکثر ۵۰۰ کیلوبایت
                                </span>
                              </div>
                              {slideImageErrors[q._clientId] && (
                                <p className="mt-2 text-sm text-red-600">
                                  {slideImageErrors[q._clientId]}
                                </p>
                              )}
                              {q.educationalImage && (
                                <div className="mt-3 flex items-start gap-3">
                                  <img
                                    src={q.educationalImage}
                                    alt="پیش‌نمایش تصویر"
                                    className="w-36 h-28 object-contain rounded-lg border border-gray-200 bg-white"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-green-700 mb-2">
                                      تصویر جدید بارگذاری شده است.
                                    </p>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        updateSlideField(
                                          q._clientId,
                                          "educationalImage",
                                          "",
                                        )
                                      }
                                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-red-600 bg-red-50 hover:bg-red-100"
                                    >
                                      <XMarkIcon className="w-4 h-4" />
                                      حذف تصویر
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>

                            <div>
                              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                                <PlayCircleIcon className="w-5 h-5 text-blue-500" />
                                لینک ویدیو
                              </label>
                              <div className="relative">
                                <input
                                  type="url"
                                  dir="ltr"
                                  value={q.videoUrl || ""}
                                  onChange={(e) =>
                                    updateSlideField(
                                      q._clientId,
                                      "videoUrl",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="https://example.com/video"
                                  className="w-full px-3 py-2.5 text-sm rounded-xl border border-gray-200 outline-none focus:ring-2 focus:ring-blue-500 text-left bg-white"
                                />
                                {q.videoUrl && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateSlideField(
                                        q._clientId,
                                        "videoUrl",
                                        "",
                                      )
                                    }
                                    title="پاک کردن لینک"
                                    className="absolute left-2 top-1/2 -translate-y-1/2 p-1 text-red-500 hover:bg-red-50 rounded-full"
                                  >
                                    <XMarkIcon className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                امتیاز پیش‌فرض
                              </label>
                              <input
                                type="number"
                                min={1}
                                value={q.defaultScore ?? 1}
                                onChange={(e) =>
                                  updateSlideField(
                                    q._clientId,
                                    "defaultScore",
                                    Number(e.target.value) || 1,
                                  )
                                }
                                className="w-32 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>

                <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    disabled={savingEdit}
                    className="rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                  >
                    انصراف
                  </button>
                  <button
                    type="submit"
                    disabled={savingEdit}
                    className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    {savingEdit ? "در حال ذخیره..." : "ذخیره تغییرات"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================================================
   کامپوننت کمکی
   ============================================================ */
const Detail = ({ label, value }) => (
  <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
    <p className="mb-1 text-xs font-semibold text-gray-500">{label}</p>
    <div className="break-words text-sm text-gray-800">{value || "-"}</div>
  </div>
);

export default HomeWorksTable;
